import {
  ArrowRight,
  ExpandMore,
  Functions as TheorySourceIcon,
  Play as PlayIcon,
  SmartButton as AiSourceIcon,
  Suggestion as SuggestionIcon,
} from '@tunasong/icons'
import type { Suggestion } from '@tunasong/models'
import {
  getChordConfidenceClassName,
  usePlayNotes,
  useTheoryCopy,
  useTheoryLevel,
  type TheoryCopy,
} from '@tunasong/music-ui'
import { ChordLib, isChordVariant } from '@tunasong/music-lib'
import type { NoteEvent } from '@tunasong/music-lib'
import { Divider, IconButton, Tooltip, Typography } from '@tunasong/ui-lib'
import { useEditor } from '@tunasong/plugin-lib'
import { useSuggestions } from '@tunasong/plugin-suggestion/ui'
import { useMusicContext } from '@tunasong/redux'
import type { NoteName } from '@tunasong/schemas'
import type { SidebarComponent } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, MouseEvent } from 'react'
import { useState } from 'react'
import styles from './sidebar-suggestion.module.css'

/**
 * Source-attribution label rendered inside the "Why this suggestion?"
 * expansion. Per Bowie's [[reference_theory_copy_register]]:
 *   - beginner  → hidden (drops the parenthetical entirely)
 *   - intermediate → glyph chip with hover/focus tooltip (Phase 1.5: Σ for theory, sparkles for AI — see Joni's §2.4 glyph spec)
 *   - advanced  → full text label
 *   - off       → row never expands (gated at the row level)
 *
 * Source strings flow from `lib/models/src/suggestion/suggestion.ts` —
 * `'theory:nextChord'` for the rule-based engine, `'lstm:cowriter'` for
 * the AI engine. Any other string is treated as "Suggested chord"
 * (neutral) per Joni's UX §2.4 fallback rule.
 *
 * Glyph pair rationale (Phase 1.5): the two engines are equal-status;
 * shape distinction (angular Σ vs radial sparkle) carries the signal so
 * the chip remains legible color-independent. Color is an accent only.
 */
const sourceLabelFor = (source: string): { full: string; aria: string; kind: 'theory' | 'ai' | 'neutral' } => {
  if (source === 'theory:nextChord') {
    return { full: 'Theory', aria: 'Theory suggestion', kind: 'theory' }
  }
  if (source === 'lstm:cowriter') {
    return { full: 'AI co-writer', aria: 'AI co-writer suggestion', kind: 'ai' }
  }
  return { full: 'Suggested chord', aria: 'Suggested chord', kind: 'neutral' }
}

interface SourceAttributionProps {
  source: string
  level: TheoryCopy['level']
}

const SourceAttribution: FC<SourceAttributionProps> = props => {
  const { source, level } = props
  if (level === 'beginner') {
    return null
  }
  const { full, aria, kind } = sourceLabelFor(source)
  if (level === 'intermediate') {
    // Neutral/unknown sources fall back to text to keep the unknown case honest.
    if (kind === 'neutral') {
      return (
        <Tooltip title={full}>
          <Typography variant="caption" className={styles.source}>
            {full}
          </Typography>
        </Tooltip>
      )
    }
    const Glyph = kind === 'theory' ? TheorySourceIcon : AiSourceIcon
    return (
      <Tooltip title={full}>
        <span
          role="img"
          aria-label={aria}
          className={kind === 'theory' ? styles.sourceGlyphTheory : styles.sourceGlyphAi}
        >
          <Glyph size={16} aria-hidden="true" />
        </span>
      </Tooltip>
    )
  }
  return (
    <Typography variant="caption" className={styles.source}>
      {full}
    </Typography>
  )
}

interface SuggestionRowProps {
  suggestion: Suggestion
}

/**
 * Per-suggestion sidebar row with click-to-expand "Why this suggestion?"
 * explainer (Phase-1 S4). Each row owns its expand/collapse state so the
 * surface doesn't need to thread a row id through the parent.
 *
 * Joni's UX spec §2.4 owns the layout. Key invariants:
 *   - The chord name is ALWAYS shown, regardless of `theoryLevel`. The
 *     theory-cue lines hide when level is `off` — the chord names don't.
 *   - The chevron is the disclosure affordance (Nielsen #6: recognition).
 *   - The row container is a `<button>` with `aria-expanded` (a11y SR
 *     announces expansion state changes).
 *   - The play button is a separate `<button>` sibling — click bubbling
 *     stops at the play button so clicking play doesn't also toggle the
 *     row expansion.
 *
 * When `theoryLevel` is `'off'`, the row is non-interactive (no expand,
 * no chevron) — name + Roman label only. The `useTheoryCopy` calls return
 * null and every explainer line drops out.
 */
const SuggestionRow: FC<SuggestionRowProps> = ({ suggestion }) => {
  const { name, source, description, chordAnalysis, transitionLexiconId, value } = suggestion
  const [expanded, setExpanded] = useState(false)
  const { playNotes } = usePlayNotes()

  // Pull the transition copy unconditionally — `useTheoryCopy` is a hook and
  // must run on every render (rules of hooks). It returns null for unknown
  // ids or `theoryLevel: 'off'`; we conditionally render based on that null.
  const transitionCopy = useTheoryCopy(transitionLexiconId ?? '')
  // Phase 1.5 (analyzer-lexicon consolidation): function-line copy resolves
  // via `useTheoryCopy(functionLexiconId)` when the analyzer attached an id.
  // Falls back to `chordAnalysis.description` when the lexicon returns null
  // (chromatic / V/X / modal-distinctive / tritone-sub) or when
  // `theoryLevel === 'off'`. Hook call is unconditional; an empty id resolves
  // to null per `useTheoryCopy` contract.
  const functionCopy = useTheoryCopy(chordAnalysis?.functionLexiconId ?? '')
  const theoryLevel = useTheoryLevel()
  const theoryOff = theoryLevel === 'off'

  const confidenceClassName = chordAnalysis ? getChordConfidenceClassName(chordAnalysis.confidence) : undefined

  const handlePlay = (e: MouseEvent) => {
    e.stopPropagation()
    // The Suggestion.value carries the chord shape (root + variant) wrapped
    // in an Element envelope. ChordLib.getNotes accepts the ChordVariant
    // shape directly — the editor `children` field is irrelevant for
    // playback. `isChordVariant` does the structural narrowing.
    if (!isChordVariant(value)) {
      return
    }
    const events: NoteEvent[] = ChordLib.getNotes(value).map(n => ({
      type: 'note',
      note: `${n}4` as NoteName,
      duration: '4n',
    }))
    playNotes(events)
  }

  const toggleExpanded = () => setExpanded(prev => !prev)
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleExpanded()
    }
  }

  // theoryLevel-gated: when the user has turned theory cues off entirely,
  // drop the expansion affordance — Joni's UX §2.4 microcopy rule for
  // `off` says "Sidebar table still shows the name and the Roman label.
  // The function/transition/source lines below the divider are hidden."
  // When level is on (any of beginner/intermediate/advanced) BUT no
  // chordAnalysis is present (e.g. no scale set), we also drop the
  // expansion — there's nothing meaningful to expand to.
  const canExpand = !theoryOff && Boolean(chordAnalysis || transitionCopy)
  // Source attribution uses the same level that drives the rest of the
  // explainer. When level is off the row never expands, so we don't reach
  // this branch.
  const sourceAttributionLevel: TheoryCopy['level'] | null = theoryOff
    ? null
    : (theoryLevel as TheoryCopy['level'])

  if (!canExpand) {
    return (
      <div className={styles.row}>
        <SuggestionIcon size={16} aria-hidden="true" className={styles.rowIcon} />
        <span className={styles.chordName}>{name}</span>
        {chordAnalysis ? (
          <span className={confidenceClassName}>{chordAnalysis.chordFunction}</span>
        ) : null}
        <IconButton
          aria-label={`Hear ${name}`}
          onClick={handlePlay}
          size="small"
          className={styles.playButton}
        >
          <PlayIcon />
        </IconButton>
      </div>
    )
  }

  const bodyId = `sidebar-suggestion-body-${name}-${source}`

  return (
    <div className={styles.rowGroup}>
      <button
        type="button"
        className={styles.row}
        aria-expanded={expanded}
        aria-controls={bodyId}
        aria-label={`Chord suggestion: ${name}. Click to expand for details.`}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
      >
        <SuggestionIcon size={16} aria-hidden="true" className={styles.rowIcon} />
        <span className={styles.chordName}>{name}</span>
        {chordAnalysis ? (
          <span className={confidenceClassName}>{chordAnalysis.chordFunction}</span>
        ) : null}
        <span className={styles.spacer} />
        <IconButton
          aria-label={`Hear ${name}`}
          onClick={handlePlay}
          size="small"
          className={styles.playButton}
        >
          <PlayIcon />
        </IconButton>
        <span className={styles.chevron} aria-hidden="true">
          {expanded ? <ExpandMore size={16} /> : <ArrowRight size={16} />}
        </span>
      </button>
      <div id={bodyId} className={styles.rowDescription}>
        {chordAnalysis ? (
          <Typography variant="caption" className={confidenceClassName}>
            {functionCopy?.body ?? chordAnalysis.description}
          </Typography>
        ) : description ? (
          <Typography variant="caption">{description}</Typography>
        ) : null}
      </div>
      {expanded ? (
        <div className={styles.expandedBody}>
          <Divider />
          {chordAnalysis ? (
            <Typography variant="caption" className={styles.line}>
              Function: {chordAnalysis.chordFunction} — {functionCopy?.body ?? chordAnalysis.description}
            </Typography>
          ) : null}
          {transitionCopy ? (
            <Typography variant="caption" className={styles.line}>
              {transitionCopy.body}
            </Typography>
          ) : null}
          {sourceAttributionLevel ? (
            <div className={styles.line}>
              <SourceAttribution source={source} level={sourceAttributionLevel} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export const SidebarSuggestion: FC<SidebarComponent> = ({}) => {
  const editor = useEditor({ allowNull: true })

  const { suggestions, loading } = useSuggestions(editor)
  const [hasScale] = useMusicContext({ editorId: editor?.id, selector: state => Boolean(state.scale?.current) })

  if (!suggestions) {
    return null
  }

  const isEmpty = suggestions.length === 0
  const emptyMessage = hasScale ? 'No theory-backed suggestions here.' : 'Set a key to see chord suggestions.'

  if (isEmpty && !loading) {
    return (
      <div className={styles.emptyState} role="status">
        <SuggestionIcon size={32} className={styles.emptyIcon} aria-hidden="true" />
        <Typography variant="body2">{emptyMessage}</Typography>
      </div>
    )
  }

  return (
    <div className={styles.list}>
      {suggestions.map((s, idx) => (
        <SuggestionRow key={`${s.source}-${s.name}-${idx}`} suggestion={s} />
      ))}
    </div>
  )
}
